import React from 'react'
import ReactDOM from 'react-dom/client'
import { BrowserRouter } from 'react-router-dom'
import App from './app/App.jsx'
import './index.css'
import { SentryErrorBoundary } from './shared/components';
import { initSentryLazy } from './utils/sentryLoader';
import { flushPendingIdentify } from './utils/posthogIdentify';
import { flushPendingEvents } from './utils/posthogEvents';
import { startPageLifecycle } from './utils/pageLifecycle';
import { mark, scheduleBootTimelineFlush } from './utils/bootTiming';
import { getAppBase } from './utils/appBase';

mark('react_mount_start');

ReactDOM.createRoot(document.getElementById('root')).render(
  <SentryErrorBoundary>
    <BrowserRouter
      basename={getAppBase()}
      future={{
        v7_startTransition: true,
        v7_relativeSplatPath: true
      }}
    >
      <App />
    </BrowserRouter>
  </SentryErrorBoundary>
);

mark('react_mount_end');

// Did the previous page die without closing (iOS / WeChat memory kill)? Reads a note
// the last page kept; one event only when it didn't end cleanly (utils/pageLifecycle.js).
startPageLifecycle();

// ========================================
// Deferred Analytics Loading
// ========================================

/**
 * Load analytics AFTER page is truly interactive
 *
 * Strategy:
 * 1. Wait for first user interaction (click/scroll/keypress)
 * 2. Use requestIdleCallback to load during browser idle time
 * 3. Fallback: 3 second timeout ensures analytics load even without interaction
 *
 * Why this matters:
 * - setTimeout(0) still runs during initial paint (browser is busy)
 * - requestIdleCallback runs when browser has nothing else to do
 * - User interaction means they're already engaged (page feels fast)
 *
 * Trade-off:
 * - Errors in first 3s (no interaction) are logged to console, not Sentry
 * - This is acceptable: user hasn't engaged yet, page is loading
 */

let analyticsLoaded = false;

const loadAnalytics = async () => {
  // Load PostHog first (production only) so Sentry init can use it for
  // bi-directional linking via the Sentry integration.
  if (import.meta.env.PROD && import.meta.env.VITE_POSTHOG_KEY) {
    try {
      const { default: posthog } = await import('posthog-js');
      const release = import.meta.env.VITE_BUILD_VERSION || 'dev';
      posthog.init(import.meta.env.VITE_POSTHOG_KEY, {
        api_host: 'https://us.i.posthog.com',
        autocapture: true,
        capture_pageview: 'history_change',
        persistence: 'localStorage',
        capture_performance: {
          web_vitals: true,
        },
        // Auth-gated app — anonymous profiles would inflate MTU bill with no
        // analytics value. Default is 'always'.
        person_profiles: 'identified_only',
        // Sentry is the single source for errors; the Sentry↔PostHog
        // integration links Sentry events to PostHog profiles, so we avoid
        // double-capture by disabling PostHog's own error handler.
        capture_exceptions: false,
        // Disable session replay — kids app, COPPA/privacy concern
        disable_session_recording: true,
      });
      posthog.register({ release });
      window.posthog = posthog;

      // Flush any user identity / events that fired before PostHog loaded
      flushPendingIdentify();
      flushPendingEvents();
    } catch (err) {
      console.warn('Failed to load PostHog:', err);
    }
  }

  // Sentry loads AFTER PostHog so its integrations array can include the
  // PostHog Sentry integration (adds 'PostHog Person URL' tag to Sentry events).
  try {
    await initSentryLazy();
  } catch (err) {
    console.warn('Failed to load Sentry:', err);
  }
};

const triggerAnalyticsLoad = () => {
  if (analyticsLoaded) return;
  analyticsLoaded = true;

  // Use idle callback if available for truly non-blocking load
  if ('requestIdleCallback' in window) {
    requestIdleCallback(loadAnalytics, { timeout: 2000 });
  } else {
    // Safari/older browsers: load immediately
    loadAnalytics();
  }
};

// Trigger on first user interaction (passive for scroll performance)
['click', 'scroll', 'keydown'].forEach((event) => {
  document.addEventListener(event, triggerAnalyticsLoad, { once: true, passive: true });
});

// Fallback: ensure analytics load after 1 second
// Shorter than 3s to complete before LCP timing window
setTimeout(triggerAnalyticsLoad, 1000);

// Start polling for PostHog availability so we can ship the boot_timeline
// event. If PostHog never loads, the navigator.sendBeacon fallback
// registered in index.html handles delivery instead.
scheduleBootTimelineFlush();

// ========================================
// Service Worker Registration (PWA install)
// ========================================
if ('serviceWorker' in navigator) {
  const swPath = `${getAppBase()}/sw.js`;
  window.addEventListener('load', () => {
    navigator.serviceWorker.register(swPath).catch(() => {});
  });
}
